/**
 * Bookings Service
 *
 * Data access layer for bookings and booking_items tables.
 * Handles all booking-related database operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';
import { fetchAllPages, dedupeById } from '../lib/fetchAllPages';
import {
  applyBookingCategoryFilter,
  categoryFromBookingState,
  type BookingCategory,
} from '../lib/bookingCategories';
import { deriveOutstandingAmount } from './analytics.service';
import { NO_PRICING_RULE, type ItemPricingRuleColumns } from './pricing.service';
import type { Database } from '../lib/database.types';

type Booking = Database['public']['Tables']['bookings']['Row'];
type BookingInsert = Database['public']['Tables']['bookings']['Insert'];
type BookingUpdate = Database['public']['Tables']['bookings']['Update'];
type BookingItem = Database['public']['Tables']['booking_items']['Row'];

/**
 * Customer record returned by search_customers RPC
 */
export interface CustomerResult {
  customer_firstname: string;
  customer_lastname: string;
  customer_email: string;
  customer_phone: string;
  language: string | null;
}

/**
 * Extended booking item with joined data
 */
export interface BookingItemWithDetails extends BookingItem {
  product_name?: string;
  product_types?: {
    product_name: string;
    product_type: string;
    location_id?: string;
    locations?: { location_name: string } | null;
  };
  inventory_items?: {
    size: string | null;
    product_type_id?: string;
    product_types?: {
      location_id?: string;
      locations?: { location_name: string } | null;
    };
  };
}

/**
 * Extended booking type with items and location info
 */
export interface BookingWithItems extends Booking {
  location_name?: string;
  pickup_address?: string;
  is_delivery_location?: boolean;
  items?: BookingItemWithDetails[];
  items_count?: number;
}

/**
 * Filters shared by the bookings list and its category counts.
 */
export interface BookingListFilters {
  locationId?: string;
  bookingState?: string;
  paymentState?: string;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
  inventoryItemId?: string;
  /** Overview tab. Omitted or 'all' fetches every category. */
  category?: BookingCategory;
}

/**
 * Apply the filters that are common to the bookings list and the category
 * counts. Deliberately excludes the category filter itself, so the counts
 * query can report every tab under the same set of other filters.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyBookingListFilters(query: any, filters?: BookingListFilters): any {
  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId);
  }

  if (filters?.bookingState) {
    query = query.eq('booking_state', filters.bookingState);
  }

  if (filters?.paymentState) {
    query = query.eq('payment_state', filters.paymentState);
  }

  if (filters?.startDate) {
    const start = filters.startDate.includes('T') ? filters.startDate : filters.startDate + 'T00:00:00';
    query = query.gte('start_date', start);
  }

  if (filters?.endDate) {
    const end = filters.endDate.includes('T') ? filters.endDate : filters.endDate + 'T23:59:59';
    query = query.lte('start_date', end);
  }

  if (filters?.searchQuery) {
    query = query.or(
      `order_id.ilike.%${filters.searchQuery}%,customer_firstname.ilike.%${filters.searchQuery}%,customer_lastname.ilike.%${filters.searchQuery}%,customer_email.ilike.%${filters.searchQuery}%`
    );
  }

  return query;
}

/**
 * Fetch all bookings with optional filters
 *
 * @param accessControl - User's access control info for merchant/location filtering
 * @param filters - Optional filters for bookings
 */
export async function getBookings(
  accessControl: AccessControl | null,
  filters?: BookingListFilters
) {
  try {
    // When filtering by a specific inventory item we first resolve the matching
    // booking ids via booking_items, then constrain the main query by id.
    // This keeps the embedded `booking_items(...)` select returning ALL items
    // on each booking row instead of only the matching one.
    let inventoryItemBookingIds: string[] | null = null;
    if (filters?.inventoryItemId) {
      const { data: itemRows, error: itemErr } = await supabase
        .from('booking_items')
        .select('booking_id')
        .eq('inventory_item_id', filters.inventoryItemId);
      if (itemErr) throw itemErr;
      inventoryItemBookingIds = Array.from(
        new Set((itemRows || []).map((r: any) => r.booking_id).filter(Boolean))
      );
      if (inventoryItemBookingIds.length === 0) {
        return { data: [], error: null };
      }
    }

    // The query is rebuilt per page: `fetchAllPages` walks `.range()` windows
    // until a short page comes back, so the overview is never silently capped
    // at the PostgREST `max-rows` limit.
    //
    // The sort carries `id` as a unique tie-breaker. Without it, rows sharing a
    // `created_at` value have no defined order between pages, which can drop or
    // duplicate rows across a page boundary.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          locations!inner(location_name, pickup_address, merchant_id, deliveries),
          booking_items(*, product_types(product_name, product_type, location_id, locations(location_name)), inventory_items(size, product_type_id, product_types!product_type_id(location_id, locations(location_name)))),
          refund_history(id)
        `)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });

      // Apply access control filtering on joined locations table
      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id'
      });

      if (inventoryItemBookingIds) {
        query = query.in('id', inventoryItemBookingIds);
      }

      query = applyBookingListFilters(query, filters);

      // Category (tab) filter — keeps the default view scoped to live bookings
      // instead of fetching the merchant's entire history on every load.
      query = applyBookingCategoryFilter(query, filters?.category);

      return query.range(from, to);
    };

    const rows = dedupeById(await fetchAllPages<any>(buildPage));

    // Format the response
    const formattedBookings = rows.map((booking: any) => ({
      ...booking,
      location_name: (booking.locations as any)?.location_name,
      pickup_address: (booking.locations as any)?.pickup_address,
      is_delivery_location: !!(booking.locations as any)?.deliveries,
      items: ((booking.booking_items as any) || []).map((item: any) => ({
        ...item,
        product_name: item.product_types?.product_name,
        product_type: item.product_types?.product_type,
        size: item.inventory_items?.size || null,
      })),
      items_count: (booking.booking_items as any)?.length || 0,
    }));

    return { data: formattedBookings, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Counts per overview tab, plus the overall total.
 */
export interface BookingCategoryCounts {
  upcoming: number;
  active: number;
  returned: number;
  cancelled: number;
  all: number;
}

/**
 * Count bookings per category under the given filters.
 *
 * The list query is scoped to one category, so the tab badges can no longer be
 * derived from the loaded rows. This selects `booking_state` only — one short
 * column per row rather than the list's deeply nested embed — which keeps the
 * payload negligible even when the merchant's full history is counted.
 *
 * The `category` field of `filters` is ignored by design: every tab is counted
 * under the same set of other filters.
 */
export async function getBookingCategoryCounts(
  accessControl: AccessControl | null,
  filters?: BookingListFilters
) {
  try {
    let inventoryItemBookingIds: string[] | null = null;
    if (filters?.inventoryItemId) {
      const { data: itemRows, error: itemErr } = await supabase
        .from('booking_items')
        .select('booking_id')
        .eq('inventory_item_id', filters.inventoryItemId);
      if (itemErr) throw itemErr;
      inventoryItemBookingIds = Array.from(
        new Set((itemRows || []).map((r: any) => r.booking_id).filter(Boolean))
      );
      if (inventoryItemBookingIds.length === 0) {
        return {
          data: { upcoming: 0, active: 0, returned: 0, cancelled: 0, all: 0 } as BookingCategoryCounts,
          error: null,
        };
      }
    }

    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select('id, booking_state, locations!inner(merchant_id)')
        .order('id', { ascending: false });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id'
      });

      if (inventoryItemBookingIds) {
        query = query.in('id', inventoryItemBookingIds);
      }

      query = applyBookingListFilters(query, filters);

      return query.range(from, to);
    };

    const rows = dedupeById(await fetchAllPages<any>(buildPage));

    const counts: BookingCategoryCounts = {
      upcoming: 0,
      active: 0,
      returned: 0,
      cancelled: 0,
      all: rows.length,
    };
    for (const row of rows) {
      counts[categoryFromBookingState(row.booking_state)] += 1;
    }

    return { data: counts, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch the next upcoming bookings for the dashboard preview widget.
 *
 * Deliberately narrow: the widget shows a handful of rows, so it asks the
 * database for exactly those instead of pulling the whole booking history and
 * slicing it client-side. Bookings are ordered by start date so the first row
 * really is the next one due.
 */
export async function getUpcomingBookings(
  accessControl: AccessControl | null,
  limit = 4
) {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        locations!inner(location_name, merchant_id),
        booking_items(id, product_types(product_name))
      `)
      .in('booking_state', ['confirmed', 'open', 'active'])
      .gte('start_date', new Date().toISOString())
      .order('start_date', { ascending: true })
      .order('id', { ascending: true })
      .limit(limit);

    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const { data, error } = await query;
    if (error) throw error;

    const formatted = (data || []).map((booking: any) => ({
      ...booking,
      location_name: booking.locations?.location_name,
      items: (booking.booking_items || []).map((item: any) => ({
        ...item,
        product_name: item.product_types?.product_name,
      })),
      items_count: booking.booking_items?.length || 0,
    }));

    return { data: formatted, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch all bookings that have a booking_item linked to a specific inventory_item.
 * Used by the inventory item detail view to show the rental history of an
 * individual asset. Returns bookings sorted by start_date descending.
 */
export async function getBookingsForInventoryItem(
  inventoryItemId: string,
  accessControl: AccessControl | null
) {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        locations!inner(location_name, merchant_id),
        booking_items!inner(*, product_types(product_name, product_type), inventory_items(item_number, nr))
      `)
      .eq('booking_items.inventory_item_id', inventoryItemId)
      .order('start_date', { ascending: false });

    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const { data, error } = await query;
    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a single booking by ID
 *
 * @param id - Booking ID
 * @param accessControl - User's access control info for merchant/location filtering
 */
export async function getBookingById(id: string, accessControl: AccessControl | null) {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        locations!inner(location_name, pickup_address, location_contact, merchant_id, self_service_enabled, deliveries, two_emails_enabled, custom_fields, merchants(currency_code, sms, whatsapp)),
        booking_items(*, product_types(product_name, product_type, inventory_type, location_id, locations(location_name)), inventory_items(nr, item_number, combination_code, product_type_id, product_types!product_type_id(location_id, locations(location_name)))),
        booking_extensions(*)
      `)
      .eq('id', id);

    // Apply access control filtering
    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const { data, error } = await query.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a booking by order ID
 *
 * @param orderId - Order ID
 * @param accessControl - User's access control info for merchant/location filtering
 */
export async function getBookingByOrderId(orderId: string, accessControl: AccessControl | null) {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        locations!inner(location_name, pickup_address, merchant_id),
        booking_items(*, product_types(product_name), inventory_items(item_number)),
        booking_extensions(*)
      `)
      .eq('order_id', orderId);

    // Apply access control filtering
    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const { data, error } = await query.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new booking with items
 *
 * CRITICAL: According to ADMIN_PANEL_OVERVIEW.md Section 6.2:
 * - NEVER create booking_item with NULL inventory_item_id
 * - Always check availability via check_availability_enhanced before calling this
 * - Each item MUST have a specific inventory_item_id assigned
 */
/**
 * Resolve `product_type` for a set of product_type_ids.
 *
 * booking_items.product_type classifies the row (bike / sup / kayak / other /
 * addon, plus bundle / bundle_component for bundle rows). It is not derivable
 * from anything else on the row, and the manage-booking app matches and deletes
 * addon rows within `product_type = 'addon'` — so a row left NULL is invisible
 * to that matcher: unmatchable, so an edit inserts a duplicate, and undeletable,
 * so the original survives. Two production bookings hit exactly that.
 *
 * Resolved here rather than at the call sites because every writer funnels
 * through this service. Passing it in from the caller is how we ended up with
 * two sites omitting it instead of one.
 *
 * Degrades to an empty map rather than throwing: a booking must not fail to
 * save because a classification tag could not be looked up. The row is then no
 * worse than it is today, and the manage app's matcher adopts untagged rows by
 * product_type_id as a backstop.
 */
async function resolveProductTypes(productTypeIds: Array<string | null | undefined>) {
  const unique = [...new Set(productTypeIds.filter((id): id is string => !!id))];
  if (unique.length === 0) return new Map<string, string>();

  const { data, error } = await supabase
    .from('product_types')
    .select('id, product_type')
    .in('id', unique);

  if (error) {
    console.error('Failed to resolve product_type for booking items:', error);
    return new Map<string, string>();
  }

  return new Map<string, string>(
    (data || [])
      .filter((pt) => pt.product_type)
      .map((pt) => [pt.id as string, pt.product_type as string])
  );
}

export async function createBooking(
  booking: BookingInsert,
  items: Array<{
    product_type_id: string;
    productName: string;
    size?: string | null;
    quantity: number;
    pricePerUnit: number;
    durationHours: number;
    inventoryItemId?: string | null;
    inventoryType: 'individual' | 'pool' | 'unlimited';
    /** The rule the item was priced under; a bundle's components get the bundle's. */
    ruleColumns?: ItemPricingRuleColumns;
    isBundle?: boolean;
    bundleComponents?: Array<{
      product_type_id: string;
      product_name: string;
      quantity: number;
      size: string | null;
      inventoryItemIds: string[];
      inventoryType: 'individual' | 'pool' | 'unlimited';
    }>;
  }>
) {
  try {
    // Validate that INDIVIDUAL inventory items have inventory_item_id assigned
    // Bundle parents are exempt — they have no direct inventory
    const itemsWithoutInventory = items.filter(item =>
      item.inventoryType === 'individual' && !item.inventoryItemId && !item.isBundle
    );
    if (itemsWithoutInventory.length > 0) {
      throw new Error(
        `Cannot create booking: ${itemsWithoutInventory.length} individual inventory item(s) missing inventory assignment. ` +
        `Individual items must have a specific inventory_item_id from check_availability_enhanced.`
      );
    }

    const { data: newBooking, error: bookingError } = await supabase
      .from('bookings')
      .insert(booking)
      .select()
      .single();

    if (bookingError) throw bookingError;

    const allCreatedItems: any[] = [];
    const regularItems = items.filter(item => !item.isBundle);
    const bundleItems = items.filter(item => item.isBundle);

    // Insert non-bundle items in batch. Bundle parents and children set their own
    // product_type ('bundle' / 'bundle_component') below and are not resolved here.
    if (regularItems.length > 0) {
      const productTypeMap = await resolveProductTypes(regularItems.map((i) => i.product_type_id));
      const bookingItems = regularItems.map((item) => ({
        booking_id: newBooking.id,
        product_type_id: item.product_type_id,
        inventory_item_id: item.inventoryItemId || null,
        item_name: item.productName,
        size: item.size || null,
        quantity: item.quantity,
        unit_price: item.pricePerUnit,
        duration_hours: item.durationHours,
        total_price: item.quantity * item.pricePerUnit,
        product_type: productTypeMap.get(item.product_type_id) ?? null,
        ...(item.ruleColumns ?? NO_PRICING_RULE),
      }));

      const { data: newItems, error: itemsError } = await supabase
        .from('booking_items')
        .insert(bookingItems)
        .select();

      if (itemsError) {
        await supabase.from('bookings').delete().eq('id', newBooking.id);
        throw itemsError;
      }
      allCreatedItems.push(...(newItems || []));
    }

    // Insert bundle items: parent first (to get ID), then children
    for (const bundleItem of bundleItems) {
      const { data: parent, error: parentError } = await supabase
        .from('booking_items')
        .insert({
          booking_id: newBooking.id,
          product_type_id: bundleItem.product_type_id,
          inventory_item_id: null,
          item_name: bundleItem.productName,
          size: null,
          quantity: 1,
          unit_price: bundleItem.pricePerUnit,
          duration_hours: bundleItem.durationHours,
          total_price: bundleItem.pricePerUnit,
          product_type: 'bundle',
          is_bundle_parent: true,
          parent_booking_item_id: null,
          ...(bundleItem.ruleColumns ?? NO_PRICING_RULE),
        })
        .select()
        .single();

      if (parentError) {
        await supabase.from('bookings').delete().eq('id', newBooking.id);
        throw parentError;
      }
      allCreatedItems.push(parent);

      if (bundleItem.bundleComponents) {
        const childRows: any[] = [];
        for (const comp of bundleItem.bundleComponents) {
          if (comp.inventoryType === 'individual') {
            for (const itemId of comp.inventoryItemIds) {
              childRows.push({
                booking_id: newBooking.id,
                product_type_id: comp.product_type_id,
                inventory_item_id: itemId,
                item_name: comp.product_name,
                size: comp.size,
                quantity: 1,
                unit_price: 0,
                duration_hours: bundleItem.durationHours,
                total_price: 0,
                product_type: 'bundle_component',
                is_bundle_parent: false,
                parent_booking_item_id: parent.id,
                ...(bundleItem.ruleColumns ?? NO_PRICING_RULE),
              });
            }
          } else {
            childRows.push({
              booking_id: newBooking.id,
              product_type_id: comp.product_type_id,
              inventory_item_id: null,
              item_name: comp.product_name,
              size: comp.size,
              quantity: comp.quantity,
              unit_price: 0,
              duration_hours: bundleItem.durationHours,
              total_price: 0,
              product_type: 'bundle_component',
              is_bundle_parent: false,
              parent_booking_item_id: parent.id,
              ...(bundleItem.ruleColumns ?? NO_PRICING_RULE),
            });
          }
        }

        if (childRows.length > 0) {
          const { data: children, error: childError } = await supabase
            .from('booking_items')
            .insert(childRows)
            .select();

          if (childError) {
            await supabase.from('bookings').delete().eq('id', newBooking.id);
            throw childError;
          }
          allCreatedItems.push(...(children || []));
        }
      }
    }

    return {
      data: {
        ...newBooking,
        booking_items: allCreatedItems,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an existing booking
 */
export async function updateBooking(id: string, updates: BookingUpdate) {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Cancel a booking
 */
export async function cancelBooking(id: string, reason?: string) {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .update({
        booking_state: 'cancelled',
        internal_notes: reason || 'Cancelled',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get booking statistics
 *
 * @param accessControl - User's access control info for merchant/location filtering
 * @param filters - Optional filters for statistics
 */
export async function getBookingStats(
  accessControl: AccessControl | null,
  filters?: {
    locationId?: string;
    startDate?: string;
    endDate?: string;
  }
) {
  try {
    // These figures are aggregated over EVERY matching booking, so the query
    // must be paged: a single unbounded request would silently stop at the
    // PostgREST row cap and understate every total below. Ordering by `id`
    // gives the pages a stable, unique sort key.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select('id, booking_state, payment_state, total_amount, amount_paid, location_id, locations!inner(merchant_id), payment_transactions(amount, transaction_type), refund_history(refund_amount, scope, restores_balance)')
        .order('id', { ascending: true });

      // Apply access control filtering
      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id'
      });

      if (filters?.locationId) {
        query = query.eq('location_id', filters.locationId);
      }

      if (filters?.startDate) {
        const start = filters.startDate.includes('T') ? filters.startDate : filters.startDate + 'T00:00:00';
        query = query.gte('start_date', start);
      }

      if (filters?.endDate) {
        const end = filters.endDate.includes('T') ? filters.endDate : filters.endDate + 'T23:59:59';
        query = query.lte('start_date', end);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // Calculate statistics — exclude cancelled/no_show from revenue
    const revenueBookings = data?.filter((b) => b.booking_state !== 'cancelled' && b.booking_state !== 'no_show') || [];

    // Outstanding: true unpaid balance per booking, using payment_transactions
    // as the source of truth for paid amount (bookings.amount_paid can drift
    // out of sync with split payments). Excludes cancelled bookings.
    //
    // Uses deriveOutstandingAmount, which measures against GROSS payments: a
    // refund reduces what the customer owes rather than turning refunded money
    // back into a receivable.
    let totalOutstanding = 0;
    let openOrdersCount = 0;
    for (const b of (data || []) as any[]) {
      if (b.booking_state === 'cancelled') continue;
      if (b.payment_state === 'refunded') continue;
      const owed = deriveOutstandingAmount(b);
      if (owed > 0.01) {
        totalOutstanding += owed;
        openOrdersCount += 1;
      }
    }

    const stats = {
      total: data?.length || 0,
      confirmed: data?.filter((b) => b.booking_state === 'confirmed').length || 0,
      active: data?.filter((b) => b.booking_state === 'active').length || 0,
      completed: data?.filter((b) => b.booking_state === 'completed').length || 0,
      cancelled: data?.filter((b) => b.booking_state === 'cancelled').length || 0,
      paid: data?.filter((b) => b.payment_state === 'paid').length || 0,
      pending: data?.filter((b) => b.payment_state === 'pending').length || 0,
      totalOutstanding,
      openOrdersCount,
      totalRevenue: revenueBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0),
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get weekly performance statistics (this week vs last week)
 *
 * @param accessControl - User's access control info for merchant/location filtering
 */
export async function getWeeklyPerformanceStats(accessControl: AccessControl | null) {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate this week's start (Sunday) and end
    const dayOfWeek = today.getDay();
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - dayOfWeek);
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
    thisWeekEnd.setHours(23, 59, 59, 999);

    // Calculate last week's start and end
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(thisWeekStart);
    lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
    lastWeekEnd.setHours(23, 59, 59, 999);

    // Query this week's bookings
    let thisWeekQuery = supabase
      .from('bookings')
      .select('id, total_amount, booking_state, created_at, location_id, locations!inner(merchant_id)')
      .gte('created_at', thisWeekStart.toISOString())
      .lte('created_at', thisWeekEnd.toISOString());

    thisWeekQuery = applyAccessControl(thisWeekQuery, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    // Query last week's bookings
    let lastWeekQuery = supabase
      .from('bookings')
      .select('id, total_amount, booking_state, created_at, location_id, locations!inner(merchant_id)')
      .gte('created_at', lastWeekStart.toISOString())
      .lte('created_at', lastWeekEnd.toISOString());

    lastWeekQuery = applyAccessControl(lastWeekQuery, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const [thisWeekResult, lastWeekResult] = await Promise.all([
      thisWeekQuery,
      lastWeekQuery
    ]);

    if (thisWeekResult.error) throw thisWeekResult.error;
    if (lastWeekResult.error) throw lastWeekResult.error;

    const thisWeekData = thisWeekResult.data || [];
    const lastWeekData = lastWeekResult.data || [];

    // Calculate this week's stats (exclude cancelled bookings for revenue)
    const thisWeekRevenue = thisWeekData
      .filter(b => b.booking_state !== 'cancelled')
      .reduce((sum, b) => sum + (b.total_amount || 0), 0);
    const thisWeekBookings = thisWeekData.filter(b => b.booking_state !== 'cancelled').length;

    // Calculate last week's stats
    const lastWeekRevenue = lastWeekData
      .filter(b => b.booking_state !== 'cancelled')
      .reduce((sum, b) => sum + (b.total_amount || 0), 0);
    const lastWeekBookings = lastWeekData.filter(b => b.booking_state !== 'cancelled').length;

    // Calculate percentage changes
    const revenueChange = lastWeekRevenue > 0
      ? Math.round(((thisWeekRevenue - lastWeekRevenue) / lastWeekRevenue) * 100)
      : thisWeekRevenue > 0 ? 100 : 0;

    const bookingsChange = lastWeekBookings > 0
      ? Math.round(((thisWeekBookings - lastWeekBookings) / lastWeekBookings) * 100)
      : thisWeekBookings > 0 ? 100 : 0;

    return {
      data: {
        thisWeekRevenue,
        thisWeekBookings,
        revenueChange,
        bookingsChange,
      },
      error: null
    };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get statistics for the next 7 days
 *
 * @param accessControl - User's access control info for merchant/location filtering
 */
export async function getNext7DaysStats(accessControl: AccessControl | null) {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Tomorrow
    const tomorrowStart = new Date(today);
    tomorrowStart.setDate(today.getDate() + 1);
    const tomorrowEnd = new Date(tomorrowStart);
    tomorrowEnd.setHours(23, 59, 59, 999);

    // Next 7 days (including today)
    const next7DaysEnd = new Date(today);
    next7DaysEnd.setDate(today.getDate() + 7);
    next7DaysEnd.setHours(23, 59, 59, 999);

    // Query bookings that start within the next 7 days
    let query = supabase
      .from('bookings')
      .select('id, total_amount, booking_state, start_date, location_id, locations!inner(merchant_id)')
      .gte('start_date', today.toISOString())
      .lte('start_date', next7DaysEnd.toISOString())
      .in('booking_state', ['confirmed', 'open', 'active']);

    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const { data, error } = await query;

    if (error) throw error;

    const bookings = data || [];

    // Calculate tomorrow's bookings
    const tomorrowBookings = bookings.filter(b => {
      const startDate = new Date(b.start_date);
      return startDate >= tomorrowStart && startDate <= tomorrowEnd;
    }).length;

    // Total bookings for next 7 days
    const thisWeekBookings = bookings.length;

    // Expected revenue for next 7 days
    const expectedRevenue = bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);

    return {
      data: {
        tomorrowBookings,
        thisWeekBookings,
        expectedRevenue,
      },
      error: null
    };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Available product type from check_availability_enhanced
 */
export interface AvailableProduct {
  item_id: string | null;
  item_number: string | null;
  product_type_id: string;
  product_name: string;
  product_type: string; // 'bike' or 'addon'
  category_id: string | null;
  size: string | null;
  inventory_type: 'individual' | 'pool' | 'unlimited';
  combination_code: string | null;
  nr: number | null;
  is_available: boolean;
  is_bundle: boolean;
  bundle_components: any | null;
  picture_url: string | null;
  pool_quantity: number | null;
  effective_pre_booking_hours: number | null;
  effective_cutoff_days_prior: number | null;
  effective_cutoff_time: string | null;
}

/**
 * Check availability using the enhanced RPC function
 *
 * Per ADMIN_PANEL_OVERVIEW.md Section 5.3 (lines 1154-1165):
 * - p_category is deprecated, always pass null
 * - All other parameters are optional but improve specificity
 * - Returns available products with specific item_id assignments
 */
export async function checkAvailability(params: {
  locationId: string;
  startDate: string; // ISO timestamp
  endDate: string; // ISO timestamp
  categoryId?: string;
  productName?: string;
  size?: string;
  quantity?: number;
  productTypeId?: string;
  excludedInventoryItemIds?: string[];
  excludedBookingId?: string;
  skipTimeGates?: boolean;
}) {
  try {
    const { data, error } = await supabase.rpc('check_availability_enhanced', {
      p_location_id: params.locationId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
      p_category: null, // Deprecated per ADMIN_PANEL_OVERVIEW.md
      p_model: params.productName || null,
      p_size: params.size || null,
      p_quantity: params.quantity || null,
      p_product_type_id: params.productTypeId || null,
      p_excluded_inventory_item_ids: params.excludedInventoryItemIds || null,
      p_excluded_booking_id: params.excludedBookingId || null,
      p_skip_time_gates: params.skipTimeGates || false,
    });

    if (error) throw error;

    return { data: data as AvailableProduct[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Check if booking items are available in a new timeframe (excluding current booking).
 * Used when changing booking dates to detect conflicts.
 *
 * Routes through check_availability_enhanced so the check includes:
 * - Overlapping bookings (with maintenance buffer)
 * - inventory_blocking_times
 * - max_concurrent_rentals caps
 * Excludes the current booking via p_excluded_booking_id so its own slot
 * doesn't count as a conflict against itself.
 */
export async function checkBookingItemsAvailability(params: {
  bookingId: string;
  locationId: string;
  startDate: string;
  endDate: string;
  inventoryItemIds: string[];
}) {
  try {
    const { data: available, error } = await checkAvailability({
      locationId: params.locationId,
      startDate: params.startDate,
      endDate: params.endDate,
      excludedBookingId: params.bookingId,
    });

    if (error) throw new Error(error);

    const availableItemIds = new Set(
      (available || [])
        .map(row => row.item_id)
        .filter((id): id is string => !!id)
    );

    const conflictingItemIds = params.inventoryItemIds.filter(
      id => !availableItemIds.has(id)
    );

    return { data: conflictingItemIds, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Check pool item availability for a new timeframe.
 * Returns product_type_ids whose pool is fully consumed in the new period.
 */
export async function checkPoolItemsAvailability(params: {
  bookingId: string;
  locationId: string;
  startDate: string;
  endDate: string;
  poolItems: Array<{ product_type_id: string; item_name: string; quantity: number }>;
}) {
  try {
    const blockedItems: string[] = [];

    // Group quantities by product_type_id (a booking may have multiple pool items of same type)
    const grouped = new Map<string, { item_name: string; totalQty: number }>();
    for (const item of params.poolItems) {
      const existing = grouped.get(item.product_type_id);
      if (existing) {
        existing.totalQty += item.quantity;
      } else {
        grouped.set(item.product_type_id, { item_name: item.item_name, totalQty: item.quantity });
      }
    }

    for (const [productTypeId, { item_name, totalQty }] of grouped) {
      // Get pool_quantity and inventory_type for this product type
      const { data: pt, error: ptError } = await supabase
        .from('product_types')
        .select('pool_quantity, inventory_type')
        .eq('id', productTypeId)
        .single();

      if (ptError || !pt) continue;

      // Unlimited inventory types have no quantity constraint — skip availability check
      if (pt.inventory_type === 'unlimited') continue;

      const poolSize = pt.pool_quantity || 0;

      // Count how many pool items of this type are booked by OTHER bookings in the new timeframe
      const { count, error: countError } = await supabase
        .from('booking_items')
        .select('id', { count: 'exact', head: true })
        .eq('product_type_id', productTypeId)
        .is('inventory_item_id', null)
        .neq('booking_id', params.bookingId)
        .filter('bookings.booking_state', 'in', '("open","active")')
        .filter('bookings.end_date', 'gte', params.startDate)
        .filter('bookings.start_date', 'lte', params.endDate)
        .not('bookings', 'is', null);

      // If supabase join filter doesn't work cleanly, fall back to explicit join query
      if (countError) {
        // Fallback: query via booking_items with inner join on bookings
        const { data: conflictRows, error: fallbackError } = await supabase
          .from('booking_items')
          .select(`
            id,
            bookings!inner(id, booking_state, start_date, end_date)
          `)
          .eq('product_type_id', productTypeId)
          .is('inventory_item_id', null)
          .neq('bookings.id', params.bookingId)
          .in('bookings.booking_state', ['open', 'active'])
          .gte('bookings.end_date', params.startDate)
          .lte('bookings.start_date', params.endDate);

        if (fallbackError) throw fallbackError;

        const usedCount = (conflictRows || []).length;
        const remaining = poolSize - usedCount;
        if (remaining < totalQty) {
          blockedItems.push(item_name);
        }
      } else {
        const usedCount = count || 0;
        const remaining = poolSize - usedCount;
        if (remaining < totalQty) {
          blockedItems.push(item_name);
        }
      }
    }

    return { data: blockedItems, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get discount codes for a merchant
 */
export async function getDiscountCodes(merchantId: string) {
  try {
    const { data, error } = await supabase
      .from('discount_codes')
      .select('*')
      .eq('merchant_id', merchantId)
      .eq('active', true)
      .order('code', { ascending: true });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Validate a discount code
 * Returns the discount code if valid, or an error message if invalid
 */
export async function validateDiscountCode(code: string, merchantId: string) {
  try {
    const { data, error } = await supabase
      .from('discount_codes')
      .select('*')
      .eq('code', code.toUpperCase())
      .eq('merchant_id', merchantId)
      .single();

    if (error) throw error;

    if (!data) {
      return { data: null, error: 'Discount code not found' };
    }

    // Check if active
    if (!data.active) {
      return { data: null, error: 'This discount code is no longer active' };
    }

    // Check validity dates
    const now = new Date();
    if (data.valid_from && new Date(data.valid_from) > now) {
      return { data: null, error: 'This discount code is not yet valid' };
    }
    if (data.valid_to && new Date(data.valid_to) < now) {
      return { data: null, error: 'This discount code has expired' };
    }

    // Check usage limit
    if (data.max_uses && data.current_uses != null && data.current_uses >= data.max_uses) {
      return { data: null, error: 'This discount code has reached its usage limit' };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Increment the usage count of a discount code
 */
export async function incrementDiscountCodeUsage(discountCodeId: string) {
  try {
    const { data, error } = await (supabase.rpc as any)('increment_discount_usage', {
      discount_code_id: discountCodeId
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    // If the RPC doesn't exist, fall back to manual increment
    try {
      const { data, error: selectError } = await supabase
        .from('discount_codes')
        .select('current_uses')
        .eq('id', discountCodeId)
        .single();

      if (selectError) throw selectError;

      const { data: updateData, error: updateError } = await supabase
        .from('discount_codes')
        .update({ current_uses: (data.current_uses || 0) + 1 })
        .eq('id', discountCodeId)
        .select()
        .single();

      if (updateError) throw updateError;

      return { data: updateData, error: null };
    } catch (fallbackError) {
      return { data: null, error: handleSupabaseError(fallbackError) };
    }
  }
}

/**
 * Delete booking items by their IDs
 */
/**
 * Update existing booking items (e.g. after duration change recalculates pricing)
 *
 * `ruleColumns`, when given, is written as a unit (see ItemPricingRuleColumns):
 * a repriced item carries its new rule, an item whose price was kept because no
 * rule covers the new duration carries all-NULL. Omitted, the columns are left
 * as they are — e.g. a quantity change does not change the tariff.
 */
export async function updateBookingItems(items: Array<{
  id: string;
  unit_price: number;
  total_price: number;
  duration_hours: number;
  quantity?: number;
  ruleColumns?: ItemPricingRuleColumns;
}>) {
  if (items.length === 0) {
    return { data: [], error: null };
  }

  try {
    // Supabase doesn't support bulk update natively, so we run individual updates
    const results = await Promise.all(
      items.map(item => {
        const payload: Record<string, unknown> = {
          unit_price: item.unit_price,
          total_price: item.total_price,
          duration_hours: item.duration_hours,
        };
        if (item.quantity !== undefined) {
          payload.quantity = item.quantity;
        }
        if (item.ruleColumns) {
          Object.assign(payload, item.ruleColumns);
        }
        return supabase
          .from('booking_items')
          .update(payload)
          .eq('id', item.id)
          .select();
      })
    );

    const firstError = results.find(r => r.error);
    if (firstError?.error) throw firstError.error;

    return { data: results.flatMap(r => r.data || []), error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function deleteBookingItems(itemIds: string[]) {
  if (itemIds.length === 0) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from('booking_items')
      .delete()
      .in('id', itemIds)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create new booking items.
 * Supports bundle parent/child: inserts parents first to get real IDs,
 * then maps temp parent IDs to real IDs for children.
 */
export async function createBookingItems(items: Array<{
  booking_id: string;
  product_type_id: string;
  item_name: string;
  size: string | null;
  quantity: number;
  unit_price: number;
  inventory_item_id: string | null;
  duration_hours: number;
  total_price: number;
  is_bundle_parent?: boolean;
  parent_booking_item_id?: string | null;
  product_type?: string | null;
  /** The rule the item was priced under; bundle components carry the bundle's. */
  ruleColumns?: ItemPricingRuleColumns;
}>) {
  if (items.length === 0) {
    return { data: [], error: null };
  }

  try {
    // Separate bundle parents, bundle children, and regular items
    const bundleParents = items.filter(i => i.is_bundle_parent);
    const bundleChildren = items.filter(i => i.parent_booking_item_id && !i.is_bundle_parent);
    const regularItems = items.filter(i => !i.is_bundle_parent && !i.parent_booking_item_id);

    const allCreated: any[] = [];

    // Insert regular items in batch. The caller's product_type wins when set (the
    // items editor knows it for some rows); anything else is resolved from the
    // product. Previously this insert dropped the column entirely, so even a
    // caller that supplied it still produced an untagged row.
    if (regularItems.length > 0) {
      const productTypeMap = await resolveProductTypes(
        regularItems.filter((i) => !i.product_type).map((i) => i.product_type_id)
      );
      const { data, error } = await supabase
        .from('booking_items')
        .insert(regularItems.map(item => ({
          booking_id: item.booking_id,
          product_type_id: item.product_type_id,
          item_name: item.item_name,
          size: item.size,
          quantity: item.quantity,
          unit_price: item.unit_price,
          inventory_item_id: item.inventory_item_id,
          duration_hours: item.duration_hours,
          total_price: item.total_price,
          product_type: item.product_type ?? productTypeMap.get(item.product_type_id) ?? null,
          ...(item.ruleColumns ?? NO_PRICING_RULE),
        })))
        .select();
      if (error) throw error;
      allCreated.push(...(data || []));
    }

    // Insert bundle parents, then their children
    // Map temp parent IDs → real DB IDs
    const tempToRealId = new Map<string, string>();

    for (const parent of bundleParents) {
      const tempId = (parent as any).id || (parent as any).tempId;
      const { data, error } = await supabase
        .from('booking_items')
        .insert({
          booking_id: parent.booking_id,
          product_type_id: parent.product_type_id,
          item_name: parent.item_name,
          size: parent.size,
          quantity: parent.quantity,
          unit_price: parent.unit_price,
          inventory_item_id: null,
          duration_hours: parent.duration_hours,
          total_price: parent.total_price,
          product_type: 'bundle',
          is_bundle_parent: true,
          parent_booking_item_id: null,
          ...(parent.ruleColumns ?? NO_PRICING_RULE),
        })
        .select()
        .single();
      if (error) throw error;
      allCreated.push(data);
      if (tempId) {
        tempToRealId.set(tempId, data.id);
      }
    }

    // Insert bundle children with mapped parent IDs
    if (bundleChildren.length > 0) {
      const childRows = bundleChildren.map(child => ({
        booking_id: child.booking_id,
        product_type_id: child.product_type_id,
        item_name: child.item_name,
        size: child.size,
        quantity: child.quantity,
        unit_price: child.unit_price,
        inventory_item_id: child.inventory_item_id,
        duration_hours: child.duration_hours,
        total_price: child.total_price,
        product_type: 'bundle_component',
        is_bundle_parent: false,
        parent_booking_item_id: tempToRealId.get(child.parent_booking_item_id!) || child.parent_booking_item_id,
        ...(child.ruleColumns ?? NO_PRICING_RULE),
      }));

      const { data, error } = await supabase
        .from('booking_items')
        .insert(childRows)
        .select();
      if (error) throw error;
      allCreated.push(...(data || []));
    }

    return { data: allCreated, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Search unique customers from previous bookings via the search_customers RPC.
 * Deduplicates by email at the DB level (most recent booking wins).
 */
export async function searchCustomers(merchantId: string, query: string, limit = 10) {
  try {
    const { data, error } = await supabase.rpc('search_customers', {
      p_merchant_id: merchantId,
      p_query: query,
      p_limit: limit,
    });

    if (error) throw error;

    return { data: (data || []) as CustomerResult[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
